import { z } from 'zod';

// Persist the inspector's dragged position per browser, so a developer who moves
// it out of the way finds it there next time rather than back over the canvas.
// Local and best-effort: a parse failure or unavailable storage falls back to
// the default (top-right anchor).

const STORAGE_KEY = 'flow-undo-inspector-chrome';

const chromeSchema = z.object({
  // Pixel position the panel was dragged to. Null means "use the default
  // top-right anchor". Pixels (not the Chakra scale) because this is a computed
  // drag offset, like a React Flow node transform, not an authored layout value.
  position: z.object({ x: z.number(), y: z.number() }).nullable(),
  // Whether the panel was open when last seen, so a developer who keeps it open
  // finds it open on the next load rather than having to re-summon it from the
  // console each time. Defaults to closed for a first-time / cleared session.
  open: z.boolean().default(false),
});

export type InspectorChrome = z.infer<typeof chromeSchema>;

const DEFAULT_CHROME: InspectorChrome = { position: null, open: false };

export function loadInspectorChrome(): InspectorChrome {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return DEFAULT_CHROME;
    }
    const parsed = chromeSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : DEFAULT_CHROME;
  } catch {
    return DEFAULT_CHROME;
  }
}

export function saveInspectorChrome(chrome: InspectorChrome): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(chrome));
  } catch {
    // Storage unavailable (private mode, quota): the inspector still works, it
    // just won't remember its position. Nothing to recover.
  }
}
